import moment from "moment";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../utils/format-sub";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import BandTag from "./BandTag";
import daysUntil from "./daysUntil";
import MemberRowActions, { isMsbUserId } from "./MemberRowActions";
import peopleNouns from "./peopleNouns";

/**
 * Requirements §6.4. These members are still active, still paying and
 * still in MRR — §5.2's "scheduled to cancel is not churn". They appear
 * here because this is the last point at which a conversation can change
 * the outcome, sorted soonest-first for that reason.
 *
 * The copy never says "cancelling at period end": `period` on the Stripe
 * flag means the member's billing cycle, which is not the reporting period
 * the rest of Income uses.
 */
const ScheduledToCancelTable = ({
  rows = [],
  currency = "usd",
  context,
  theme = "dark",
  controls = null,
  // { push: bool, press: bool } — community DMs on for that product
  dmEnabled = {},
}) => {
  const { t } = useTranslation("coaching-portal");
  // All spans both products, so each row messages through its own.
  const chatType = (row) =>
    context === "all" ? String(row.product || "").toLowerCase() : context;

  return (
    <>
      <header className="table-header">
        <hgroup>
          <div className="flex items-center gap-2">
            <h3 className="title">
              {t("income.attention.scheduledTableTitle")}
            </h3>
            <BandTag kind="snapshot" label={t("income.bands.now")} />
          </div>
        </hgroup>
        {controls}
      </header>
      {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
      <table role="report-table" className="scheduled-to-cancel-table">
        {/* biome-ignore lint/a11y/useValidAriaRole: table-header used for structure */}
        <thead role="table-header">
          <tr>
            <th>{t(peopleNouns(context).column)}</th>
            <th>{t("income.attention.plan")}</th>
            <th>{t("income.attention.mrrLeaving")}</th>
            <th>{t("income.attention.totalPaid")}</th>
            <th>{t("income.attention.memberSince")}</th>
            <th>{t("income.attention.endsOn")}</th>
            <th>{t("income.attention.daysRemaining")}</th>
            <th className="text-center">{t("income.attention.action")}</th>
          </tr>
        </thead>
        {rows.length > 0 ? (
          <tbody>
            {rows.map((row) => (
              <tr key={row.subscriptionId}>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="image-wrapper">
                      {row.customerName ? (
                        <img
                          src={getAvatarUrl(
                            { name: row.customerName, avatar: row.avatar },
                            null,
                            theme
                          )}
                          className="w-8 h-8 rounded-full"
                          alt={t("income.avatarAlt")}
                        />
                      ) : (
                        <img
                          style={{ width: 32, height: 32, borderRadius: 32 }}
                          src="/img/avatars/registration-pending2.svg"
                          className="icon-grey"
                          alt=""
                        />
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="subscriber-text">
                        {row.customerName ||
                          row.customerEmail ||
                          row.customerId}
                      </span>
                      {row.customerName && row.customerEmail && (
                        <span className="text-xs text-(--white-70)">
                          {row.customerEmail}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td>{row.planName || row.planId}</td>
                <td>{formatNumber(row.mrr, row.currency || currency)}</td>
                <td>
                  {formatNumber(row.totalPaid || 0, row.currency || currency)}
                </td>
                <td className="whitespace-nowrap">
                  {row.memberSince
                    ? moment(row.memberSince).format("MMM D, YYYY")
                    : "—"}
                </td>
                <td className="whitespace-nowrap">
                  {row.endsAt ? moment(row.endsAt).format("MMM D, YYYY") : "—"}
                </td>
                <td>
                  {daysUntil(row.endsAt) == null
                    ? "—"
                    : daysUntil(row.endsAt) === 0
                      ? t("income.attention.endsToday")
                      : t("income.attention.inDays", {
                          count: daysUntil(row.endsAt),
                        })}
                </td>
                <td>
                  <MemberRowActions
                    member={row}
                    currency={row.currency || currency}
                    chatType={chatType(row)}
                    canMessage={
                      dmEnabled[chatType(row)] && isMsbUserId(row.customerId)
                    }
                    t={t}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan="100%">
                <div className="body-table-empty">
                  <p>{t("income.attention.noScheduled")}</p>
                </div>
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </>
  );
};

export default ScheduledToCancelTable;
